import { type QueryClient } from '@tanstack/react-query';

import { EMAIL_ACCOUNTS_QUERY_KEY, type EmailAccount } from '@/hooks/use-email-accounts';
import { FILTER_COUNTS_KEY } from '@/hooks/use-email-filter-counts';
import { resetRevokedDocumentSources } from '@/hooks/use-document-sources';
import { evictFetchedEmailBodies } from '@/hooks/use-fetch-email-body';
import { invalidateReportingAggregates } from '@/hooks/reporting-aggregate-invalidation';
import { reportingKeys } from '@/hooks/use-reporting-query-keys';
import {
  CORRESPONDENCE_NAMESPACE_EVICT_PREFIXES,
  queryKeyMatchesAnyPrefix,
} from '@/lib/share-gated-cache-registry';

import {
  correspondenceKeys,
  invalidateSavedViewCounts,
} from '../correspondence/correspondence-query-keys';
import { readShareRevokedAt, readShareString } from './share-payload-utils';

import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

/**
 * Recipient-side correspondence share-access cache logic, shared by the app-root
 * ShareAccessRealtime provider. A correspondence share confers access to
 * constituent email content, so revocation must remove every cache that could
 * still render it. The share-gated surfaces are declared once in
 * `share-gated-cache-registry` (the same source `shouldDehydrateQuery` reads);
 * a drift-guard test asserts this handler evicts every registry prefix, so
 * eviction and persistence-exclusion cannot diverge.
 */

export interface ShareRevocationEvent {
  readonly correspondenceId: string | undefined;
  readonly organizationId: string | undefined;
}

function queryKeyIncludes(queryKey: readonly unknown[], value: string): boolean {
  return queryKey.includes(value);
}

export function removeCorrespondenceAccessCaches(
  queryClient: QueryClient,
  event: ShareRevocationEvent,
): void {
  const { correspondenceId, organizationId } = event;
  if (!correspondenceId) {
    return;
  }

  // The reporting drill-down caches subject lines it read under the recipient's
  // RLS. Its key carries the row ids, so it cannot be matched per-row — and the
  // cache is small and short-lived, so dropping the prefix is the cheap
  // correct move.
  // `resetQueries`, not `removeQueries`: removing the entry leaves a MOUNTED
  // observer still holding the map it already had, so an open drill-down keeps
  // rendering the title that was just revoked. Reset clears it AND refetches
  // the active observer, which re-derives the list from RLS.
  queryClient.resetQueries({ queryKey: reportingKeys.drilldownRowDetailAll() }).catch(() => {
    /* eviction is best-effort; the next read re-derives from RLS regardless */
  });

  if (organizationId) {
    queryClient.removeQueries({
      queryKey: correspondenceKeys.shares(organizationId, correspondenceId),
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: correspondenceKeys.detail(organizationId, correspondenceId),
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: correspondenceKeys.capabilities(organizationId, correspondenceId),
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: correspondenceKeys.interactions(organizationId, correspondenceId),
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: correspondenceKeys.draftHistory(organizationId, correspondenceId),
      exact: true,
    });
    queryClient.removeQueries({
      queryKey: correspondenceKeys.timelineEntity(organizationId, correspondenceId),
    });
    // Thread-level comments are keyed by the joined email ids
    // (['comments','thread-list', orgId, sortedEmailIds, filters]), NOT the
    // correspondenceId, so the corrId predicate below cannot target them - evict
    // the org's thread-comment namespace so comment excerpts on the revoked
    // thread cannot survive. Safe: rare revoke, refetch re-gates under RLS.
    queryClient.removeQueries({ queryKey: ['comments', 'thread-list', organizationId] });
  }

  queryClient.removeQueries({
    predicate: ({ queryKey }) =>
      queryKeyIncludes(queryKey, correspondenceId) &&
      (queryKey[0] === 'comments' || queryKey[0] === 'email-tasks'),
  });

  // Documents linked to the thread render on its timeline and in its side
  // panel, read through a link table whose policy requires view_thread. Their
  // key is ['documents', org, viewer, 'for-correspondence', correspondenceId]
  // (documentKeys.forCorrespondence) — outside the `correspondence` root, so
  // nothing above reaches it. Reset, not remove, for the same reason as the
  // drill-down: an open panel's observer would keep the rows it already has.
  // Scoped to this one thread; the Knowledge lists under `documents` are not
  // share-gated.
  queryClient
    .resetQueries({
      predicate: ({ queryKey }) =>
        queryKey[0] === 'documents' &&
        queryKey[3] === 'for-correspondence' &&
        queryKey[4] === correspondenceId,
    })
    .catch(() => {
      /* eviction is best-effort; the next read re-derives from RLS regardless */
    });

  // A Pulse-written document's Sources section stores the thread's subject as a label, read
  // under a policy that requires access to the record it names.
  resetRevokedDocumentSources(queryClient, { kind: 'email', correspondenceId }).catch(() => {
    /* eviction is best-effort; the next read re-derives from RLS regardless */
  });

  // A body fetched without being stored is keyed by message, not correspondence,
  // so the predicate above cannot reach it; evict every one, including results
  // of fetches still in flight.
  evictFetchedEmailBodies(queryClient).catch(() => {
    /* eviction is best-effort; the next fetch re-checks access regardless */
  });

  // `email-drafts-thread` (keyed by threadId) and `task-linked-correspondence`
  // (keyed by taskId) are NOT keyed by the correspondenceId, so the predicate
  // above cannot target them - evict the whole namespace, driven by the registry
  // set (see share-gated-cache-registry for the refetch-on-mount safety rationale).
  queryClient.removeQueries({
    predicate: ({ queryKey }) =>
      queryKeyMatchesAnyPrefix(queryKey, CORRESPONDENCE_NAMESPACE_EVICT_PREFIXES),
  });
}

export function invalidateRevocationListCaches(
  queryClient: QueryClient,
  event: ShareRevocationEvent,
): void {
  const { organizationId } = event;

  if (organizationId) {
    queryClient
      .invalidateQueries({ queryKey: correspondenceKeys.index(organizationId) })
      .catch(() => {
        // Best-effort invalidation failure is non-critical.
      });
    queryClient.invalidateQueries({ queryKey: [FILTER_COUNTS_KEY, organizationId] }).catch(() => {
      // Best-effort invalidation failure is non-critical.
    });
    invalidateSavedViewCounts(queryClient, organizationId);
    // What the person can see is what the reporting counts count.
    invalidateReportingAggregates(queryClient, organizationId);
  }

  queryClient.invalidateQueries({ queryKey: ['email-index'] }).catch(() => {
    // Best-effort invalidation failure is non-critical.
  });
  queryClient.invalidateQueries({ queryKey: ['assignments'] }).catch(() => {
    // Best-effort invalidation failure is non-critical.
  });
  queryClient.invalidateQueries({ queryKey: ['email-assignments'] }).catch(() => {
    // Best-effort invalidation failure is non-critical.
  });
}

/**
 * True when the share row rides on a shared mailbox. A share there confers
 * nothing — org membership already grants every capability — so neither a
 * grant nor a revocation of it changes what the recipient can see, and
 * surfacing the revocation as "Access removed" is simply wrong. Resolves the
 * account from the already-cached accounts list; an unresolvable account
 * (someone else's personal inbox, or a cold cache) reads as NOT shared so the
 * real revocation path still runs. REPLICA IDENTITY FULL on
 * correspondence_shares puts email_account_id in `old` on DELETE.
 * See docs/plans/shared-inbox-redundant-assignment-shares.md.
 */
export function isSharedAccountSharePayload(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
  queryClient: Pick<QueryClient, 'getQueryData'>,
): boolean {
  if (payload.table !== 'correspondence_shares') {
    return false;
  }
  const emailAccountId =
    readShareString(payload.new, 'email_account_id') ??
    readShareString(payload.old, 'email_account_id');
  const organizationId =
    readShareString(payload.new, 'organization_id') ??
    readShareString(payload.old, 'organization_id');
  if (!emailAccountId || !organizationId) {
    return false;
  }
  const accounts = queryClient.getQueryData<readonly Pick<EmailAccount, 'id' | 'account_type'>[]>([
    EMAIL_ACCOUNTS_QUERY_KEY,
    organizationId,
  ]);
  return (
    accounts?.some(
      (account) => account.id === emailAccountId && account.account_type === 'shared',
    ) ?? false
  );
}

export function isCorrespondenceRevocationPayload(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
): boolean {
  if (payload.table !== 'correspondence_shares') {
    return false;
  }

  if (payload.eventType === 'UPDATE') {
    const oldRevoked = readShareRevokedAt(payload.old);
    const newRevoked = readShareRevokedAt(payload.new);
    return oldRevoked === null && typeof newRevoked === 'string';
  }

  if (payload.eventType === 'DELETE') {
    return readShareRevokedAt(payload.old) === null;
  }

  return false;
}

export function isCorrespondenceActiveAccessPayload(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
): boolean {
  if (payload.table !== 'correspondence_shares') {
    return false;
  }

  if (payload.eventType === 'INSERT') {
    return readShareRevokedAt(payload.new) === null;
  }

  if (payload.eventType === 'UPDATE') {
    return readShareRevokedAt(payload.new) === null;
  }

  return false;
}

export function readShareRevocationEvent(
  payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
): ShareRevocationEvent {
  return {
    correspondenceId:
      readShareString(payload.new, 'correspondence_id') ??
      readShareString(payload.old, 'correspondence_id'),
    organizationId:
      readShareString(payload.new, 'organization_id') ??
      readShareString(payload.old, 'organization_id'),
  };
}
